import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { countdownForDisplay, countdownMessage } from '../utils/mcsDisplay';
import { isPreviousResult } from '../utils/summaryProgress';
import { useSummaryWait } from '../hooks/useSummaryWait';
import {
  employeeApi,
  voiceRecordsApi,
  type DailyReport,
  type MCSResponse,
  type Trend,
  type VoiceRecord,
} from '../api/client';

function formatTrends(trends: Trend[] | undefined | null): string {
  if (!trends?.length) return '';
  return trends.map((t) => `${t.direction} ${t.label}`).join(' ');
}

/**
 * EMP-S02 コンディションスコア / 振り返り（仕様 0422 Phase A 対応）。
 * 「結果を表示する」で開く今日の結果の画面（#174）。今回の録音がまとめに入るまでは「解析中」と出し、
 * 前回のまとめは「前回の結果」と分かる形で出す。入ったら、この画面のまま今回の結果に変わる。
 */
export default function ConditionScore() {
  const { t } = useTranslation();
  const { getToken } = useAuth();
  const [report, setReport] = useState<DailyReport | null>(null);
  const [nextGenerationAt, setNextGenerationAt] = useState<string | null>(null);
  const [todayRecords, setTodayRecords] = useState<VoiceRecord[]>([]);
  const [mcs, setMcs] = useState<MCSResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [retryKey, setRetryKey] = useState(0);

  const loadData = useCallback((background = false) => {
    const token = getToken();
    if (!token) {
      setLoading(false);
      return () => {};
    }
    // まとめを追いかける間の読み直しでは、画面を「読み込み中」に戻さない
    if (!background) {
      setError(null);
      setLoading(true);
    }
    let cancelled = false;
    const now = new Date();
    const y = now.getFullYear(), m = now.getMonth(), d = now.getDate();
    Promise.all([
      employeeApi.getTodayReport(token).catch((e) => e as Error),
      employeeApi.getMcs(token).catch((e) => e as Error),
      // 一覧が読めなくても結果は出す（今回の録音が入ったかを見分けられないだけ）
      voiceRecordsApi
        .getList(token, {
          from: new Date(y, m, d, 0, 0, 0, 0).toISOString(),
          to: new Date(y, m, d, 23, 59, 59, 999).toISOString(),
          limit: 10,
        })
        .catch(() => null),
    ]).then(([reportRes, mcsRes, recordsRes]) => {
      if (cancelled) return;
      if (reportRes instanceof Error) {
        setReport(null);
        setNextGenerationAt(null);
      } else {
        setReport(reportRes.report);
        setNextGenerationAt(reportRes.nextGenerationAt);
      }
      setTodayRecords(recordsRes?.data ?? []);
      if (mcsRes instanceof Error) setMcs(null);
      else setMcs(mcsRes);
      setError(reportRes instanceof Error || mcsRes instanceof Error ? t('dashboard.errorLoad') : null);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [getToken, t]);

  useEffect(() => {
    const cleanup = loadData();
    return () => {
      if (typeof cleanup === 'function') cleanup();
    };
  }, [loadData, retryKey]);

  const isNew = report?.schema === 'new';
  const isLegacy = report?.schema === 'legacy';
  const domains = isNew ? report?.domains : undefined;
  const calm = isNew ? report?.calm : undefined;
  const dayOverDay = isNew ? report?.dayOverDay : undefined;
  const coldStart = isNew ? report?.coldStart : undefined;

  // 月次論文版 MCS（仕様 0422 §6 / Phase B + 2026-04-26 改訂）
  const mcsAvailable = mcs?.available === true && mcs.mcs_score != null;
  const mcsScore = mcsAvailable ? mcs!.mcs_score! : null;
  const mcsMode = mcs?.mode_applied ?? null;
  const mcsCutoffVersion = mcs?.cutoff_version ?? null;
  const mcsYearMonth = mcs?.year_month ?? null;
  const mcsDataVolumePct = mcs?.data_volume_flag != null ? Math.round(mcs.data_volume_flag * 100) : null;
  const mcsIsReference = mcsMode === 'short_reference';
  const mcsShowScore = mcsAvailable && (mcsMode === 'standard' || mcsMode === 'short_reference');
  // 課題 #61: 録音日数での判定を撤去（#44 確定）。共通ロジックへ集約
  // 残り時間は日次レポートの値（直近 28 日）を使う。当日のレポートが無いときだけ月次の値（#61）
  const mcsCountdown = countdownMessage(countdownForDisplay(report, mcs));

  // 旧スキーマ用（月次 comment は新スキーマで monthly_comment、レガシーは trends）
  const trendText = isLegacy ? formatTrends(report?.trends ?? null) || null : null;
  const conditionEmoji = isLegacy ? (report?.condition_emoji ?? report?.condition ?? null) : null;
  const comment = report?.comment ?? null;

  // 今回の録音がまとめに入るまでは「解析中」。まとめが無いときは、解析の済んだ録音があれば待つ（#174）
  const showingPreviousResult = isPreviousResult(report?.lastRecordedAt, todayRecords);
  const noSummaryYet = report == null && todayRecords.some((r) => r.analysis_json != null);
  const analyzing = !loading && (showingPreviousResult || noSummaryYet);
  const reloadInBackground = useCallback(() => {
    loadData(true);
  }, [loadData]);
  const summaryWait = useSummaryWait(analyzing, nextGenerationAt, reloadInBackground);
  const previousBadge = showingPreviousResult && (
    <span className="previous-result-badge">{t('dashboard.summaryWait.previousBadge')}</span>
  );

  if (loading) {
    return (
      <div className="page score-page">
        <div className="dashboard-loading">{t('common.loading')}</div>
      </div>
    );
  }

  return (
    <div className="page score-page">
      <h1>{t('dashboard.scoreTitle')}</h1>

      {analyzing && (
        <div className="score-card score-analyzing" role="status" aria-live="polite" data-testid="result-analyzing">
          <h3>{t('dashboard.summaryWait.analyzing')}</h3>
          <p className="score-text">{t(summaryWait.message.key, summaryWait.message.params)}</p>
          {showingPreviousResult && report?.lastRecordedAt && (
            <p className="score-text-muted" data-testid="previous-result-note">
              {t('dashboard.summaryWait.previousNote', {
                time: new Date(report.lastRecordedAt).toLocaleTimeString('ja-JP', {
                  hour: '2-digit',
                  minute: '2-digit',
                }),
              })}
            </p>
          )}
        </div>
      )}

      {error && (
        <div className="score-error" role="alert">
          <p>{error}</p>
          <button type="button" className="dashboard-error-retry" onClick={() => setRetryKey((k) => k + 1)}>
            {t('dashboard.retry')}
          </button>
        </div>
      )}

      {/* === 新スキーマ === */}
      {isNew && domains && (coldStart?.show_4_domains ?? true) && (
        <div className="score-card">
          <h3>{t('dashboard.domains.title')}{previousBadge}</h3>
          <div className="score-domains-grid">
            <div className="score-domain">
              <div className="score-domain-label">{t('dashboard.domains.vitality')}</div>
              <div className="score-stat">{Math.round(domains.vitality)}</div>
            </div>
            <div className="score-domain">
              <div className="score-domain-label">{t('dashboard.domains.tension')}</div>
              <div className="score-stat">{Math.round(domains.tension)}</div>
            </div>
            <div className="score-domain">
              <div className="score-domain-label">{t('dashboard.domains.cognition')}</div>
              <div className="score-stat">{Math.round(domains.cognition)}</div>
              <div className="score-domain-meta">
                {t('dashboard.domains.cognitionModeLabel')}: {domains.cognition_mode}
              </div>
            </div>
            <div className="score-domain">
              <div className="score-domain-label">{t('dashboard.domains.emotionStability')}</div>
              <div className="score-stat">{Math.round(domains.emotion_stability)}</div>
            </div>
          </div>
        </div>
      )}

      {isNew && calm && (coldStart?.show_calm ?? true) && (
        <div className={`score-card score-calm score-calm-${calm.label}`}>
          <h3>{t('dashboard.calm.title')}{previousBadge}</h3>
          <p className="score-text">
            {t(`dashboard.calm.level${calm.level}`)} ／ {t('dashboard.calm.atmosphereAvg', { value: Math.round(calm.atmosphere_avg) })}
          </p>
        </div>
      )}

      {isNew && dayOverDay && (coldStart?.show_day_over_day ?? true) && (
        <div className="score-card">
          <h3>{t('dashboard.dayOverDay.title')}{previousBadge}</h3>
          <p className="score-text">
            {dayOverDay.direction === 'up' && t('dashboard.dayOverDay.up')}
            {dayOverDay.direction === 'down' && t('dashboard.dayOverDay.down')}
            {dayOverDay.direction === 'flat' && t('dashboard.dayOverDay.flat')}
            {dayOverDay.direction == null && t('dashboard.dayOverDay.unknown')}
            {dayOverDay.direction != null && typeof dayOverDay.delta_pct === 'number' && (
              <>
                {' '}
                ({dayOverDay.delta_pct >= 0 ? '+' : ''}
                {dayOverDay.delta_pct.toFixed(1)}%)
              </>
            )}
          </p>
        </div>
      )}

      {/* === 月次論文版 MCS（Phase B / 仕様 0422 §6 + 2026-04-26 改訂） === */}
      <div className="score-card">
        <h3>{t('dashboard.monthlyMcs.title')}</h3>
        {mcsShowScore && mcsScore != null ? (
          <>
            {/* 「高リスク／低リスク」とその色分けは、言い方を見直すまで出さない（課題 #101-9） */}
            <div className="score-stat" data-testid="mcs-value">
              {mcsScore.toFixed(3)}
            </div>
            <div className="score-stat-label">{t('dashboard.monthlyMcs.scoreUnit')}</div>
            {mcsIsReference && (
              <span className="mcs-reference-badge" role="status">
                {t('dashboard.monthlyMcs.referenceBadge')}
              </span>
            )}
            <div className="score-mcs-meta">
              {mcsYearMonth && (
                <p>{t('dashboard.monthlyMcs.month', { value: mcsYearMonth })}</p>
              )}
              {mcsDataVolumePct != null && (
                <p>{t('dashboard.monthlyMcs.dataVolume', { percent: mcsDataVolumePct })}</p>
              )}
              {mcsCutoffVersion && (
                <p>
                  {t(`dashboard.monthlyMcs.cutoff.${mcsCutoffVersion}`, {
                    defaultValue: mcsCutoffVersion,
                  })}
                </p>
              )}
            </div>
            {mcsIsReference && (
              <p className="score-text-muted">{t('dashboard.monthlyMcs.referenceHint')}</p>
            )}
            {mcs?.monthly_comment && <p className="score-text">{mcs.monthly_comment}</p>}
            {mcs?.remarks && <p className="score-text-muted">{mcs.remarks}</p>}
          </>
        ) : (
          <>
            <p className="score-text">
              {t(mcsCountdown.key, mcsCountdown.params)}
            </p>
            <p className="score-text-muted">{t('dashboard.monthlyMcs.countdownHint')}</p>
          </>
        )}
      </div>

      {/* === 旧スキーマ（後方互換）+ レポート未取得時のフォールバック === */}
      {(isLegacy || report == null) && (
        <div className="score-grid">
          <div className="score-card">
            <h3>{t('dashboard.trend')}</h3>
            <p className="score-text">{trendText ? `↑ ${trendText}` : '—'}</p>
          </div>
          <div className="score-card">
            <h3>{t('dashboard.conditionToday')}</h3>
            <p className="score-emoji" aria-label={t('dashboard.conditionLabel')}>
              {conditionEmoji ?? '—'}
            </p>
            <div className="score-stat-label">
              {conditionEmoji ? t('dashboard.apiLinked') : t('dashboard.noConditionToday')}
            </div>
          </div>
        </div>
      )}

      <div className="score-card">
        <h3>{t('dashboard.aiComment')}{previousBadge}</h3>
        <p className="score-text">{comment || t('dashboard.noReportYet')}</p>
      </div>

      <div className="score-card">
        <h3>{t('dashboard.qaTitle')}</h3>
        <p className="score-intro">{t('dashboard.qaIntro')}</p>
      </div>

      <p className="score-back">
        <Link to="/dashboard">{t('common.back')} → {t('employee.dashboard')}</Link>
      </p>
    </div>
  );
}
